import { useState } from 'react';
import { syncAllData } from '@/lib/services/sync-service';
import { toast } from 'sonner';

export const useSyncState = () => {
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [syncError, setSyncError] = useState<Error | null>(null);
  const [syncAttempts, setSyncAttempts] = useState(0);
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState(0);

  const syncAllDataWithStatus = async (userId: string) => {
    if (syncInProgress) {
      if (import.meta.env.DEV) console.log('Sync already in progress, skipping');
      return;
    }

    const now = Date.now();
    if (now - lastSyncTimestamp < 10000 && syncAttempts > 0) {
      if (import.meta.env.DEV) console.log('Recent sync detected, skipping');
      return;
    }
    
    try {
      setSyncInProgress(true);
      setSyncError(null);
      setSyncAttempts(prev => prev + 1);
      setLastSyncTimestamp(now);
      
      if (import.meta.env.DEV) console.log('Starting data sync');
      
      await syncAllData();
      
      if (import.meta.env.DEV) console.log('Data sync completed successfully');
    } catch (err) {
      console.error('Error syncing data:', err);
      setSyncError(err instanceof Error ? err : new Error(String(err)));
      
      toast.error('Sync error. Please try again.');
      
      throw err;
    } finally {
      setSyncInProgress(false);
    }
  };

  const refreshData = async (user: { id: string } | null) => {
    if (!user) {
      if (import.meta.env.DEV) console.log('No user to refresh data for');
      return;
    }
    
    try {
      toast.info('Refreshing your data...');
      await syncAllDataWithStatus(user.id);
      toast.success('Data refreshed successfully');
    } catch (err) {
      console.error('Error refreshing data:', err);
      toast.error('Failed to refresh your data');
    }
  };

  return {
    syncInProgress,
    syncError,
    syncAttempts,
    syncAllDataWithStatus,
    refreshData,
    lastSyncTimestamp
  };
};
